// src/components/shopping/CustomShoppingList.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Grid,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Divider,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Autocomplete from "@mui/material/Autocomplete";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import { useParams, useNavigate } from "react-router-dom";
import { premiumOptionSx } from "./common/autocompleteStyles";


import api from "../services/api"; // προσαρμόζεις στο μονοπάτι σου
import { useLocationCtx } from "../context/LocationContext";
import LocationGate from "./LocationGate";
import { userHasActiveEntitlement } from "../utils/entitlements";

// 🔹 modal με αναλυτικές προσφορές (μόνο EU/GR)
import ChangeProductModal from "./shopping/ChangeProductModal";
import InstacartCheckoutButton from "./client/modal/InstacartCheckoutButton";

// ----------------- Βοηθητικά για νόμισμα / βάρος / πακέτα ----------------- //

function getCurrencySymbolByCode(code, countryId) {
  const c = (code || "").toUpperCase();
  if (c === "USD") return "$";
  if (c === "EUR") return "€";
  if (c === "GBP") return "£";
  if (c === "RON") return "lei";
  if (c === "HUF") return "Ft";
  if (c === "BGN") return "лв";
  if (c === "MKD") return "ден";
  if (c === "ALL") return "Lek";
  if (countryId === 2 || countryId === "2") return "$";
  return "€";
}

function formatWeight(grams) {
  const v = Number(grams || 0);
  if (!v) return "";
  if (v >= 1000) return `${(v / 1000).toFixed(2)} kg`;
  return `${v.toFixed(0)} g`;
}

function formatPackageLabel(offer) {
  if (!offer) return "";
  if (offer.package_size_text) return offer.package_size_text;
  if (offer.normalized_qty) return formatWeight(offer.normalized_qty);
  return "";
}

const CustomShoppingList = () => {
  const { lang: urlLang } = useParams();
  const { t, i18n } = useTranslation();

  // ✅ define lang once (fix eslint no-undef)
  const lang = (urlLang || i18n.language || "en").toLowerCase();
  // optional: if you ever pass "gr" in routes, normalize it to "el"
  const normalizedLang = lang === "gr" ? "el" : lang;

  // backend expects "gr" instead of "el"
  const apiLang = normalizedLang === "el" ? "gr" : normalizedLang;

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { country, usZip, usState } = useLocationCtx();
  const [geo, setGeo] = useState(null);
  const navigate = useNavigate();

  const isUS = country === 2 || country === "2";
  const showCost = true; // ✅ US compliance: no prices/totals/stores/brands

  const [openGate, setOpenGate] = useState(false);

  const [genericProducts, setGenericProducts] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [newItem, setNewItem] = useState({
    product: null,
    quantity: "",
    unit: "unit",
  });

  const [previewItems, setPreviewItems] = useState([]); // αποτέλεσμα από /preview
  const [globalBestTotal, setGlobalBestTotal] = useState(null);
  const [currency, setCurrency] = useState("EUR");

  const [listName, setListName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalText, setErrorModalText] = useState("");
  const [errorModalTitle, setErrorModalTitle] = useState("");



  const [user, setUser] = useState(null);
  const [checkingUser, setCheckingUser] = useState(true);

  const showErrorModal = (msg, title) => {
    const finalTitle = title || t("required_fields_modal_title") || (i18n.language === "el" ? "Χρειάζονται όλα τα πεδία" : "Missing required fields");
    setError(msg);
    setErrorModalTitle(finalTitle);
    setErrorModalText(msg);
    setErrorModalOpen(true);
  };
  // ✅ Selection (checkboxes) – default: όλα τσεκαρισμένα
  const [selectedIds, setSelectedIds] = useState([]);



  useEffect(() => {
    Promise.allSettled([api.users.getCurrentUser(), api.users.entitlements()])
      .then(([userResult, entitlementsResult]) => {
        if (userResult.status !== "fulfilled") {
          setUser(null);
          return;
        }
        const res = userResult.value;
        const u = res.data?.data || res.data?.user || res.data;
        const entitlements = entitlementsResult.status === "fulfilled"
          ? entitlementsResult.value?.data?.entitlements || null
          : null;
        setUser(u ? { ...u, entitlements } : null);
      })
      .catch(() => setUser(null))
      .finally(() => setCheckingUser(false));
  }, []);

  const isLoggedIn = !!user;
  const isPremium = userHasActiveEntitlement(user);

  const isEU = !isUS;
  const canChangeProduct = isEU && isPremium;

  const currencySymbol = getCurrencySymbolByCode(currency, country);

  const zipFromGeo = geo?.zip_code || geo?.us_zip || null;
  const stateFromGeo = geo?.us_state || null;

  const zipToShow = zipFromGeo || usZip || null;
  const stateToShow = stateFromGeo || usState || null;

  // ----------------- Φόρτωμα generic products ----------------- //
  useEffect(() => {
    api.genericProducts
      .list()
      .then((res) => {
        const data = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];

        setGenericProducts(data);

        if (newItem.product && newItem.product.id) {
          const match = data.find((p) => p.id === newItem.product.id);
          setNewItem((prev) => ({ ...prev, product: match || null }));
        }
      })
      .catch(() => setGenericProducts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const getProductLabel = (gp) => {
    if (!gp) return "";
    if (gp.name) return gp.name;
    return i18n.language === "el"
      ? gp.name_el || gp.name_gr || gp.name_en || ""
      : gp.name_en || gp.name_el || gp.name_gr || "";
  };

  // ✅ default: όλα selected (όσα έχουν generic_product_id)
  function buildInitialSelectedIds(items) {
    return (items || [])
      .filter((it) => it && it.generic_product_id)
      .map((it) => String(it.generic_product_id));
  }

  const toggleSelected = (gpId) => {
    if (!gpId) return;
    const id = String(gpId);
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ----------------- Διαχείριση "πρώτου βήματος" (εισαγωγή items) ----------------- //
  const handleAddItem = () => {
    setError("");
    setSuccess("");

    const hasProduct = !!newItem.product;
    const qtyStr = String(newItem.quantity ?? "").trim();

    if (!hasProduct || qtyStr === "") {
      showErrorModal(
        t("all_fields_required") || "Συμπλήρωσε προϊόν και ποσότητα (σε γραμμάρια ή ml)",
        t("required_fields_modal_title") || "Χρειάζονται όλα τα πεδία"
      );
      return;
    }

    const normalizedQty = qtyStr.replace(",", ".");
    const quantity = Number(normalizedQty);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      showErrorModal(
        t("invalid_quantity") ||
        "Μη έγκυρη ποσότητα. Βάλε έναν θετικό αριθμό (π.χ. 100, 500, 1000)",
        t("invalid_quantity_title") ||
        (i18n.language === "el" ? "Μη έγκυρη ποσότητα" : "Invalid quantity")
      );
      return;
    }

    const id = Date.now();
    const gpId = newItem.product.id;

    setRawItems((prev) => [
      ...prev,
      {
        id,
        generic_product_id: gpId,
        product_name: getProductLabel(newItem.product),
        quantity,
        unit: newItem.unit || "unit",
      },
    ]);

    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (gpId) s.add(String(gpId));
      return Array.from(s);
    });

    setNewItem({ product: null, quantity: "", unit: "unit" });
  };


  const handleRemoveRawItem = (id) => {
    setRawItems((prev) => {
      const removed = prev.find((x) => x.id === id);
      const next = prev.filter((x) => x.id !== id);

      // ✅ remove from selection too
      if (removed?.generic_product_id) {
        setSelectedIds((sel) => sel.filter((x) => x !== String(removed.generic_product_id)));
      }

      return next;
    });
  };

  // ----------------- Κλήση backend για preview ----------------- //
  const handlePreview = async () => {
    if (!country) {
      setOpenGate(true);
      return;
    }
    setError("");
    setSuccess("");
    setPreviewItems([]);
    setGlobalBestTotal(null);

    if (!rawItems.length) {
      showErrorModal(t("add_at_least_one_item") || "Πρόσθεσε τουλάχιστον ένα προϊόν");
      return;
    }


    const payload = {
      lang: i18n.language,
      country_id: country ? Number(country) : undefined,
      items: rawItems.map((item) => ({
        generic_product_id: item.generic_product_id,
        quantity: item.quantity,
        unit: item.unit,
      })),
    };

    try {
      setLoadingPreview(true);
      const res = await api.shoppingList.previewCustom(payload);
      if (!res.data.success) {
        setError(res.data.error || "Αποτυχία υπολογισμού λίστας");
        return;
      }

      setGeo(res.data.geo || null);
      setCurrency(res.data.currency || "EUR");
      setGlobalBestTotal(res.data.global_best_total);

      const itemsFromBackend = res.data.items || [];

      // ✅ always: default selection = all items
      setSelectedIds(buildInitialSelectedIds(itemsFromBackend));

      // EU/GR: keep offer selection (cheapest)
      if (!isUS) {
        const withSelection = itemsFromBackend.map((it) => {
          let selectedOfferIndex = -1;
          if (it.offers && it.offers.length) {
            let min = it.offers[0].total_cost;
            selectedOfferIndex = 0;
            it.offers.forEach((off, idx) => {
              if (off.total_cost < min) {
                min = off.total_cost;
                selectedOfferIndex = idx;
              }
            });
          }
          return { ...it, selectedOfferIndex };
        });
        setPreviewItems(withSelection);
      } else {
        // ✅ US: keep items but ignore offers/prices UI
        setPreviewItems(itemsFromBackend.map((it) => ({ ...it, selectedOfferIndex: -1 })));
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Σφάλμα στο preview λίστας");
    } finally {
      setLoadingPreview(false);
    }
  };

  // ----------------- Επιλογή store ανά προϊόν (μόνο EU/GR) ----------------- //
  const handleChangeOffer = (lineIndex, offerIndex) => {
    if (isUS) return;
    setPreviewItems((prev) =>
      prev.map((item) =>
        item.line_index === lineIndex
          ? { ...item, selectedOfferIndex: offerIndex }
          : item
      )
    );
  };

  // ----------------- Grouping ανά σούπερ μάρκετ (μόνο EU/GR) ----------------- //
  const groupedByStore = useMemo(() => {
    if (isUS) return [];
    const map = new Map();

    previewItems.forEach((item) => {
      if (!item.offers || item.offers.length === 0) return;
      const idx = item.selectedOfferIndex;
      if (idx === -1 || !item.offers[idx]) return;

      const offer = item.offers[idx];
      const key =
        offer.store_id ||
        offer.store_location_id ||
        offer.store_chain ||
        offer.store_name ||
        "unknown_store";

      if (!map.has(key)) {
        map.set(key, {
          storeId: offer.store_id,
          storeName: offer.store_name,
          items: [],
          total: 0,
        });
      }

      const bucket = map.get(key);
      bucket.items.push({
        ...item,
        selectedOffer: offer,
      });
      bucket.total += offer.total_cost;
    });

    return Array.from(map.values()).map((b) => ({
      ...b,
      total: Math.round(b.total * 100) / 100,
    }));
  }, [previewItems, isUS]);

  const globalSelectedTotal = useMemo(() => {
    if (isUS) return null;
    return groupedByStore.reduce((sum, g) => sum + g.total, 0).toFixed(2);
  }, [groupedByStore, isUS]);

  const previewSavings = useMemo(() => {
    const selected = Number(globalSelectedTotal || 0);
    const best = Number(globalBestTotal || 0);
    if (!Number.isFinite(selected) || !Number.isFinite(best) || selected <= best) return null;
    return (selected - best).toFixed(2);
  }, [globalSelectedTotal, globalBestTotal]);

  // ✅ Instacart ids: from checkboxes (US)
  const selectedGenericIdsForInstacart = useMemo(() => {
    if (!isUS) return [];
    // if preview exists, allow sending selected only; else rawItems selected
    if (selectedIds?.length) return selectedIds;
    // fallback
    return rawItems
      .filter((x) => x.generic_product_id)
      .map((x) => String(x.generic_product_id));
  }, [isUS, selectedIds, rawItems]);

  // ----------------- Modal state & handlers (μόνο EU/GR) ----------------- //
  const [activeModalItem, setActiveModalItem] = useState(null);

  const handleOpenModalForItem = (item) => {
    if (isUS) return;

    // ✅ block guests & free users
    if (!canChangeProduct) {
      // δείξε το FeatureGate (login ή premium)
      // (εδώ έχεις ήδη error modal infra – αλλά καλύτερα FeatureGateDialog αν το έχεις)
      const msg = !isLoggedIn
        ? (t("feature_requires_login") || "Για να αλλάξεις προϊόν/μάρκα, χρειάζεται σύνδεση.")
        : (t("premium_required_desc") || "Η αλλαγή προϊόντος/μάρκας είναι διαθέσιμη μόνο στο Premium.");

      showErrorModal(msg, t("premium_feature_title") || "Premium λειτουργία");
      return;
    }

    setActiveModalItem(item);
  };


  const handleCloseModal = () => {
    setActiveModalItem(null);
  };

  const handleModalOfferSelect = (lineItem, variant) => {
    if (isUS) return;
    if (!canChangeProduct) {
      const msg = !isLoggedIn
        ? (t("feature_requires_login") || "Για να αλλάξεις προϊόν/μάρκα, χρειάζεται σύνδεση.")
        : (t("premium_required_desc") || "Η αλλαγή προϊόντος/μάρκας είναι διαθέσιμη μόνο στο Premium.");
      showErrorModal(msg, t("premium_feature_title") || "Premium λειτουργία");
      return;
    }

    if (!lineItem || !variant) return;

    const normalizedQty = Number(variant.normalized_qty || 0);
    const price = Number(variant.price || variant.unit_price || 0);
    const packagesNeeded = Number(variant.packages_needed || 0);
    const basketQtyG = Number(variant.basket_qty_g || variant.basket_qty_ml || variant.basket_qty_piece || 0);
    const leftoverG = Number(variant.leftover_qty_g || variant.leftover_qty_ml || variant.leftover_qty_piece || 0);
    const basketCost = Number(variant.pack_cost ?? variant.basket_cost ?? variant.total_cost ?? 0);
    const recipeCost = Number(variant.cost_for_recipe_qty ?? variant.theoretical_cost ?? basketCost);
    const unitKind =
      variant.unit_kind ||
      variant.unit_price_kind ||
      ((variant.normalized_unit || "").toLowerCase() === "piece"
        ? "piece"
        : variant.price_per_liter != null
          ? "l"
          : variant.price_per_kg != null
            ? "kg"
            : null);
    const unitPrice =
      variant.unit_price ??
      variant.unit_price_per_kg ??
      variant.price_per_piece ??
      variant.price_per_kg ??
      variant.price_per_liter ??
      price ??
      null;

    const currencyCode = variant.currency || currency || "EUR";

    const productName =
      variant.product_full_label ||
      variant.product_name ||
      variant.brand_name_en ||
      variant.brand_name_el ||
      lineItem.product_name;

    const newOffer = {
      store_id:
        variant.store_location_id ||
        variant.store_chain ||
        variant.store_name ||
        "unknown_store",
      store_name: variant.store_name || "",
      store_chain: variant.store_chain || "",
      store_location_id: variant.store_location_id,
      store_provider: variant.store_provider,
      currency: currencyCode,

      product_name: productName,
      product_full_label: productName,
      brand_name_en: variant.brand_name_en,
      brand_name_el: variant.brand_name_el,

      theoretical_cost: Number(recipeCost.toFixed(2)),
      basket_cost: Number(basketCost.toFixed(2)),
      calculated_cost: Number(recipeCost.toFixed(4)),
      total_cost: Number(recipeCost.toFixed(2)),

      packages_needed: packagesNeeded,
      basket_qty_g: Number(basketQtyG.toFixed(2)),
      leftover_qty_g: Number(leftoverG.toFixed(2)),
      normalized_qty: normalizedQty || null,
      normalized_unit: (variant.normalized_unit || "").toLowerCase() || null,
      variant_price: price || null,
      unit_price: unitPrice != null ? Number(unitPrice) : null,
      unit_kind: unitKind,
      unit_price_kind: unitKind,
      pricing_path: variant.pricing_path || null,
      price_per_kg: variant.price_per_kg != null ? Number(variant.price_per_kg) : null,
      price_per_liter: variant.price_per_liter != null ? Number(variant.price_per_liter) : null,
      price_per_piece: variant.price_per_piece != null ? Number(variant.price_per_piece) : null,

      is_instacart:
        variant.is_instacart ||
        variant.store_location_id === "instacart_us_generic" ||
        variant.store_provider === "serpapi_instacart",
      product_id: variant.product_id,
      product_variant_id: variant.product_variant_id || variant.variant_id || null,
      external_id: variant.external_id,
    };

    setPreviewItems((prev) =>
      prev.map((it) => {
        if (it.line_index !== lineItem.line_index) return it;

        const oldOffers = it.offers || [];
        const existingIdx = oldOffers.findIndex(
          (o) =>
            o.product_variant_id &&
            newOffer.product_variant_id &&
            o.product_variant_id === newOffer.product_variant_id
        );

        let newOffers;
        let selectedOfferIndex;

        if (existingIdx >= 0) {
          newOffers = oldOffers.map((o, idx) => (idx === existingIdx ? newOffer : o));
          selectedOfferIndex = existingIdx;
        } else {
          newOffers = [...oldOffers, newOffer];
          selectedOfferIndex = newOffers.length - 1;
        }

        return { ...it, offers: newOffers, selectedOfferIndex };
      })
    );

    setActiveModalItem(null);
  };

  // ----------------- Αποθήκευση λίστας (premium) ----------------- //
  const handleSaveList = async () => {
    setError("");
    setSuccess("");

    if (!listName.trim()) {
      showErrorModal(t("enter_list_name") || "Δώσε ένα όνομα στη λίστα σου");
      return;
    }
    if (!previewItems.length) {
      showErrorModal(t("preview_before_save") || "Κάνε πρώτα υπολογισμό (preview) και μετά αποθήκευση");
      return;
    }

    // ✅ US: saving still allowed (if you want), but DO NOT store prices
    const itemsForSave = [];

    previewItems.forEach((item) => {
      const idx = item.selectedOfferIndex;
      const offer = item.offers && item.offers[idx] ? item.offers[idx] : null;

      if (isUS) {
        itemsForSave.push({
          generic_product_id: item.generic_product_id,
          quantity: item.quantity,
          unit: item.unit,
          // no store/product/price fields
        });
      } else {
        itemsForSave.push({
          generic_product_id: item.generic_product_id,
          quantity: item.quantity,
          unit: item.unit,
          store_id: offer?.store_id || null,
          store_product_id: offer?.product_variant_id || null,
          chosen_price: offer?.unit_price || null,
          chosen_total_cost: offer?.total_cost || null,
        });
      }
    });

    const payload = {
      name: listName,
      currency,
      source: "custom",
      items: itemsForSave,
      lang: apiLang,

    };

    try {
      setSaving(true);
      const res = await api.shoppingList.createCustom(payload);

      if (!res.data.success) {
        setError(res.data.error || "Αποτυχία αποθήκευσης λίστας");
        return;
      }

      setSuccess(
        res.data.message ||
          lang === "el" || lang === "gr"
          ? t("saved_selected_to_list") || "Αποθηκεύτηκε η λίστα με τα επιλεγμένα προϊόντα."
          : t("saved_selected_to_list") || "Saved selected items to your shopping list."
      );
      navigate(`/${normalizedLang}/client/select-shopping-list`);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Σφάλμα κατά την αποθήκευση της λίστας");
    } finally {
      setSaving(false);
    }
  };

  const formatQuantity = (qty, unit) => {
    if (qty == null) return "";
    const value = Number(qty) || 0;
    const u = (unit || "").toLowerCase().trim();

    if (!u || u === "unit") {
      if (value >= 1000) {
        const kg = value / 1000;
        const kgStr = Number.isInteger(kg) ? kg.toFixed(0) : kg.toFixed(2);
        return `${kgStr} kg`;
      }
      return `${value} g`;
    }
    return `${value} ${unit}`;
  };

  // ----------------- UI ----------------- //
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Τίτλος + premium badge */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h5" gutterBottom>
          {t("custom_shopping_list_title") || "Νέα Λίστα Ψωνιών (Premium Pro)"}
        </Typography>
        {isPremium && (
          <Chip
            label={t("premium_active") || "Premium ενεργό – χωρίς διαφημίσεις"}
            color="success"
            variant="outlined"
            size="small"
          />
        )}
      </Box>

      {/* Κορυφαίο μήνυμα για guest / free / premium */}
      {!checkingUser && !isLoggedIn && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t("custom_list_guest_info_title") || "Φτιάξε τη δική σου έξυπνη λίστα αγορών"}
          </Typography>
          <Typography variant="body2">
            {t("custom_list_guest_info_body") ||
              "Ως επισκέπτης μπορείς να δοκιμάσεις τον υπολογισμό. Για να αποθηκεύεις λίστες και να έχεις χωρίς διαφημίσεις, δημιούργησε λογαριασμό και αναβάθμισε σε Premium."}
          </Typography>
        </Alert>
      )}

      {!checkingUser && isLoggedIn && !isPremium && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
            border: (th) => `1px solid ${th.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t("custom_list_free_user_title") || "Χρησιμοποιείς την έξυπνη λίστα ως δωρεάν χρήστης"}
          </Typography>
          <Typography variant="body2">
            {t("custom_list_free_user_body") ||
              "Μπορείς να υπολογίζεις από ποια σούπερ μάρκετ σε συμφέρει. Με Premium ξεκλειδώνεις επιπλέον εργαλεία."}
          </Typography>
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {openGate && <LocationGate forceOpen onClose={() => setOpenGate(false)} />}

      {/* Top Ad — μόνο για επισκέπτες & free users */}
      {/* {!isPremium && (
            <Box sx={{ mb: 2 }}>
                <AdSenseAd slot="2080997069" isPremium={isPremium} />
            </Box>
        )} */}

      {/* US info note */}
      {isUS && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography fontWeight={700}>
            {t("us_estimate_note_title") || "US mode (Instacart-compliant)"}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t("us_estimate_note_body") ||
              "We show generic items and quantities only. Final product selection & pricing happens inside Instacart."}
          </Typography>
        </Alert>
      )}

      <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
        <Typography fontWeight={700}>
          {t("shopping_savings_trust_title") || "Basket savings are shown before checkout"}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {isUS
            ? (t("shopping_savings_trust_note_us") ||
              "US lists use USD and local provider coverage where available; final product selection still happens with the checkout provider.")
            : (t("shopping_savings_trust_note") ||
              "We compare your selected basket against the cheapest available supermarket variants and show the savings in EUR before you shop.")}
        </Typography>
      </Alert>

      {/* ΒΗΜΑ 1: Εισαγωγή προϊόντων */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
        <Typography variant="subtitle1" gutterBottom>
          {t("add_items_to_list") || "Προσθήκη προϊόντων στη λίστα"}
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={genericProducts || []}
              value={newItem.product || null}
              onChange={(_, value) =>
                setNewItem((prev) => ({
                  ...prev,
                  product: value || null,
                }))
              }
              isOptionEqualToValue={(opt, val) => (opt?.id ?? null) === (val?.id ?? null)}
              getOptionLabel={(opt) => getProductLabel(opt) || ""}

              renderOption={(props, option) => (
                <Box component="li" {...props} sx={premiumOptionSx(theme)}>
                  {getProductLabel(option)}
                </Box>
              )}

              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("ingredient") || "Υλικό"}
                  variant="outlined"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={t("quantity") || "Ποσότητα"}
              type="number"
              value={newItem.quantity}
              onChange={(e) =>
                setNewItem((prev) => ({
                  ...prev,
                  quantity: e.target.value,
                }))
              }
              fullWidth
              helperText={
                t("quantity_hint") ||
                "Βάλε ποσότητα σε γραμμάρια (g) ή ml, π.χ. 500, 1000."
              }
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              label={t("unit") || "Μονάδα"}
              value={newItem.unit}
              onChange={(e) =>
                setNewItem((prev) => ({
                  ...prev,
                  unit: e.target.value,
                }))
              }
              fullWidth
              placeholder="kg, g, ml, pcs..."
              helperText={t("unit_hint") || 'Άφησέ το κενό ή "unit" αν δίνεις ποσότητα σε g.'}
            />
          </Grid>
          <Grid item xs={12} sx={{ textAlign: "right" }}>
            <Button variant="contained" onClick={handleAddItem} startIcon={<ShoppingBasketIcon />}>
              {t("add") || "Προσθήκη"}
            </Button>
          </Grid>
        </Grid>

        {rawItems.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("current_items") || "Τρέχουσα λίστα προϊόντων"}
            </Typography>

            {rawItems.map((item) => {
              const checked = item.generic_product_id
                ? selectedIds.includes(String(item.generic_product_id))
                : false;

              return (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 0.5,
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => toggleSelected(item.generic_product_id)}
                    />
                    <Typography>
                      <b>{item.product_name}</b> — {formatQuantity(item.quantity, item.unit)}
                    </Typography>
                  </Box>

                  <IconButton size="small" onClick={() => handleRemoveRawItem(item.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}

            {/* Select/Deselect all (premium touch) */}
            <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  const all = rawItems
                    .filter((x) => x.generic_product_id)
                    .map((x) => String(x.generic_product_id));
                  setSelectedIds(Array.from(new Set(all)));
                }}
              >
                {t("select_all") || (i18n.language === "el" ? "Επιλογή όλων" : "Select all")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedIds([])}
              >
                {t("deselect_all") || (i18n.language === "el" ? "Αποεπιλογή όλων" : "Deselect all")}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* ΒΗΜΑ 2: Preview */}
      <Box sx={{ mb: 3, textAlign: "right" }}>
        <Button
          variant="outlined"
          onClick={handlePreview}
          disabled={loadingPreview || !rawItems.length}
        >
          {loadingPreview
            ? t("calculating") || "Υπολογισμός..."
            : t("calculate_best_supermarkets") || "Υπολόγισε από πού συμφέρει"}
        </Button>
      </Box>

      {/* ΒΗΜΑ 3: Προβολή */}
      {previewItems.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
          {/* US geo note */}
          {isUS && (zipToShow || stateToShow) && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2">
                {t("offers_for_zip") || "Περιοχή"}:{" "}
                <b>
                  {zipToShow || "-"}
                  {stateToShow ? ` (${stateToShow})` : ""}
                </b>
              </Typography>
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant="subtitle1">
              {t("shopping_list_preview") || "Προεπισκόπηση λίστας"}
            </Typography>

            {/* Totals chips only EU/GR */}
            {showCost && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {globalSelectedTotal && (
                  <Chip
                    label={`${t("total_selected") || "Σύνολο επιλογών"}: ${globalSelectedTotal} ${currencySymbol}`}
                    color="primary"
                    variant="outlined"
                  />
                )}
                {globalBestTotal && (
                  <Chip
                    label={`${t("global_best_total") || "Απόλυτα ελάχιστο"}: ${globalBestTotal} ${currencySymbol}`}
                    color="success"
                    variant="outlined"
                  />
                )}
                {previewSavings && (
                  <Chip
                    label={`${t("potential_savings") || "Potential savings"}: ${previewSavings} ${currencySymbol}`}
                    color="success"
                  />
                )}
              </Box>
            )}
          </Box>

          {/* US: clean list (no stores/offers/prices) */}
          {isUS ? (
            <Box>
              {previewItems.map((item) => {
                const checked = item.generic_product_id
                  ? selectedIds.includes(String(item.generic_product_id))
                  : false;

                const label =
                  item.generic_name ||
                  item.generic_product_name ||
                  item.product_name ||
                  t("ingredient") ||
                  "Item";

                return (
                  <Box
                    key={item.line_index || item.generic_product_id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      py: 1,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleSelected(item.generic_product_id)}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatQuantity(item.quantity, item.unit)}
                          {item.match_status ? ` • ${t(item.match_status) || item.match_status}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            // EU/GR: existing offers UI
            <>
              {previewItems.map((item) => (
                <Box key={item.line_index} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    {item.product_name} — {formatQuantity(item.quantity, item.unit)}
                  </Typography>

                  {(!item.offers || !item.offers.length) && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        mb: 0.5,
                        fontWeight: 700,
                        color: "error.main",
                      }}
                    >
                      {t("no_offers_found_nearby") ||
                        t("no_offers_found") ||
                        "Δεν βρέθηκαν προϊόντα στην περιοχή σας για αυτό το υλικό."}
                    </Typography>
                  )}

                  {item.offers && item.offers.length > 0 && (
                    <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                      <Grid item xs={12} md={9}>
                        <FormControl fullWidth size="small">
                          <InputLabel>
                            {t("choose_supermarket") || "Επίλεξε σούπερ μάρκετ"}
                          </InputLabel>
                          <Select
                            label={t("choose_supermarket") || "Επίλεξε σούπερ μάρκετ"}
                            value={item.selectedOfferIndex >= 0 ? item.selectedOfferIndex : ""}
                            onChange={(e) =>
                              handleChangeOffer(item.line_index, Number(e.target.value))
                            }
                          >
                            {item.offers.map((off, idx) => {
                              const mainLabel =
                                off.product_full_label ||
                                off.product_name ||
                                off.brand_name ||
                                t("unknown_product") ||
                                "Άγνωστο προϊόν";

                              const storeLabel =
                                off.store_name || t("unknown_store") || "Άγνωστο κατάστημα";

                              const pkgLabel = formatPackageLabel(off);
                              const pkgs =
                                typeof off.packages_needed === "number"
                                  ? off.packages_needed
                                  : null;

                              const leftoverLabel =
                                typeof off.leftover_qty_g === "number" && off.leftover_qty_g > 0
                                  ? formatWeight(off.leftover_qty_g)
                                  : "";

                              return (
                                <MenuItem key={idx} value={idx}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.3,
                                      width: "100%",
                                    }}
                                  >
                                    <Typography variant="body2">{mainLabel}</Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: 12, color: "text.secondary" }}
                                    >
                                      {storeLabel}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontSize: 12, color: "text.secondary" }}
                                    >
                                      {pkgs && pkgLabel ? `${pkgs} × ${pkgLabel}` : pkgLabel || ""}
                                      {leftoverLabel &&
                                        ` — ${t("leftover_qty") || "Θα περισσέψει"}: ${leftoverLabel}`}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600 }}>
                                      {Number(off.total_cost || 0).toFixed(2)} {currencySymbol}
                                      {idx === item.selectedOfferIndex ? " ✓" : ""}
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        sx={{ textAlign: { xs: "left", md: "right" } }}
                      >
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<InfoOutlinedIcon />}
                          onClick={() => handleOpenModalForItem(item)}
                        >
                          {canChangeProduct
                            ? (t("view_product_options") || "Προβολή προϊόντων & τιμών")
                            : (t("unlock_product_change") || "Ξεκλείδωσε αλλαγή προϊόντος (Premium)")}
                        </Button>

                      </Grid>
                    </Grid>
                  )}
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              {groupedByStore.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("per_supermarket_breakdown") || "Ανάλυση ανά σούπερ μάρκετ"}
                  </Typography>
                  {groupedByStore.map((g) => (
                    <Box key={g.storeId} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? "primary.light" : "primary.main",
                        }}
                      >
                        {g.storeName} — {g.total.toFixed(2)} {currencySymbol}
                      </Typography>

                      {g.items.map((it) => {
                        const key = `${g.storeId}-${it.line_index}`;
                        const offer = it.selectedOffer;
                        const offerLabel =
                          offer.product_full_label ||
                          offer.product_name ||
                          offer.brand_name ||
                          t("unknown_product") ||
                          "Άγνωστο προϊόν";

                        const pkgLabel = formatPackageLabel(offer);
                        const pkgs =
                          typeof offer.packages_needed === "number" ? offer.packages_needed : null;
                        const leftoverLabel =
                          typeof offer.leftover_qty_g === "number" && offer.leftover_qty_g > 0
                            ? formatWeight(offer.leftover_qty_g)
                            : "";

                        return (
                          <Typography key={key} variant="body2" sx={{ ml: 2 }}>
                            • {offerLabel}: {formatQuantity(it.quantity, it.unit)}{" "}
                            {pkgs && pkgLabel ? `— ${pkgs} × ${pkgLabel}` : pkgLabel ? `— ${pkgLabel}` : ""}{" "}
                            {leftoverLabel &&
                              `— ${t("leftover_qty") || "Θα περισσέψει"}: ${leftoverLabel}`}{" "}
                            — {Number(offer.total_cost || 0).toFixed(2)} {currencySymbol}
                          </Typography>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}

          {/* ✅ Instacart CTA – only US */}
          {isUS && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="subtitle2">
                  {t("instacart_checkout_title") || "Online αγορά μέσω Instacart (ΗΠΑ)"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("instacart_checkout_hint") ||
                    "Θα ανοίξει το καλάθι σου στο Instacart. Εκεί θα διαλέξεις μάρκες/εναλλακτικές πριν την παραγγελία."}
                </Typography>
              </Box>

              <InstacartCheckoutButton
                context="custom_list"
                selectedGenericIds={selectedGenericIdsForInstacart}
                items={previewItems
                  .filter(it => selectedIds.includes(String(it.generic_product_id)))
                  .map(it => ({
                    generic_product_id: it.generic_product_id,
                    qty: Number(it.quantity),
                    unit: it.unit || "g",
                  }))
                }
              />

            </Box>
          )}
        </Paper>
      )}

      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            p: 0.5,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {errorModalTitle || (t("required_fields_modal_title") || "Χρειάζονται όλα τα πεδία")}
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ mb: 1 }}>
            {errorModalText || error}
          </Typography>

          {/* optional “premium hint” look */}
          {!isPremium && (
            <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
              <Typography variant="body2">
                {t("required_fields_modal_hint") ||
                  (i18n.language === "el"
                    ? "Tip: Με Premium έχεις εμπειρία χωρίς διαφημίσεις και περισσότερα εργαλεία."
                    : "Tip: Premium removes ads and unlocks more tools.")}
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => setErrorModalOpen(false)}
          >
            {t("ok_got_it") || (i18n.language === "el" ? "ΟΚ, κατάλαβα" : "OK, got it")}
          </Button>
        </DialogActions>
      </Dialog>


      {/* ΒΗΜΑ 4: Αποθήκευση (Premium) */}
      {previewItems.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
          <Typography variant="subtitle1" gutterBottom>
            {t("save_shopping_list_premium") || "Αποθήκευση λίστας (Premium)"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("premium_required_hint") ||
              "Η αποθήκευση λίστας είναι διαθέσιμη σε premium χρήστες."}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                label={t("list_name") || "Όνομα λίστας"}
                fullWidth
                value={listName}
                onChange={(e) => setListName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
              <Button variant="contained" onClick={handleSaveList} disabled={saving || !isPremium}>
                {saving ? t("saving") || "Αποθήκευση..." : t("save_list") || "Αποθήκευση λίστας"}
              </Button>
              {!isPremium && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }} color="text.secondary">
                  {t("premium_upgrade_hint") || "Απαιτείται Premium συνδρομή για αποθήκευση."}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}


      {/* Bottom Ad — μόνο για επισκέπτες και free users */}
      {/* {!isPremium && (
            <Box sx={{ mt: 3 }}>
                <AdSenseAd slot="2080997069" isPremium={isPremium} />
            </Box>
        )} */}

      {/* ✅ Modal only non-US */}
      {!isUS && (
        <ChangeProductModal
          open={!!activeModalItem}
          onClose={handleCloseModal}
          offers={activeModalItem?.all_variants || activeModalItem?.offers || []}
          quantity={activeModalItem?.quantity}
          unit={activeModalItem?.unit}
          lang={i18n.language}
          bestOfferProductVariantId={
            activeModalItem &&
              activeModalItem.selectedOfferIndex >= 0 &&
              activeModalItem.offers &&
              activeModalItem.offers[activeModalItem.selectedOfferIndex]
              ? activeModalItem.offers[activeModalItem.selectedOfferIndex].product_variant_id
              : null
          }
          productName={activeModalItem?.product_name}
          onSelectOffer={(offer) => handleModalOfferSelect(activeModalItem, offer)}
        />
      )}

    </Container>

  );
};

export default CustomShoppingList;
